'use client';

import { useState, useMemo } from 'react';
import { Search, Filter, Download } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { stockBalances, warehouses, items, getItemById } from '../data/mock-data';
import { getStockStatus, STOCK_STATUS_LABELS, STOCK_STATUS_COLORS } from '../types';
import type { StockBalance as StockBalanceType, StockStatus } from '../types';

export function StockBalancePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<StockStatus | 'all'>('all');

  const filteredData = useMemo(() => {
    return stockBalances.filter((sb) => {
      // 검색 필터
      const matchesSearch =
        sb.itemCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sb.itemName.toLowerCase().includes(searchQuery.toLowerCase());

      // 창고 필터
      const matchesWarehouse =
        selectedWarehouse === 'all' || sb.warehouseId === selectedWarehouse;

      // 상태 필터
      const item = getItemById(sb.itemId);
      const status = item
        ? getStockStatus(sb.quantity, item.minStock, item.maxStock)
        : 'normal';
      const matchesStatus = selectedStatus === 'all' || status === selectedStatus;

      return matchesSearch && matchesWarehouse && matchesStatus;
    });
  }, [searchQuery, selectedWarehouse, selectedStatus]);

  const columns = [
    { key: 'itemCode', label: '품목코드', width: '10%', sortable: true },
    { key: 'itemName', label: '품목명', width: '20%', sortable: true },
    { key: 'warehouseName', label: '창고', width: '12%', sortable: true },
    {
      key: 'quantity',
      label: '현재 재고',
      width: '10%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown, row: StockBalanceType) => {
        const item = getItemById(row.itemId);
        const status = item
          ? getStockStatus(row.quantity, item.minStock, item.maxStock)
          : 'normal';
        return (
          <span className={STOCK_STATUS_COLORS[status] + ' font-medium'}>
            {value as number}
          </span>
        );
      },
    },
    {
      key: 'reservedQuantity',
      label: '예약',
      width: '10%',
      align: 'right' as const,
      render: (value: unknown) => (
        <span className="text-muted-foreground">{value as number}</span>
      ),
    },
    {
      key: 'availableQuantity',
      label: '가용',
      width: '10%',
      align: 'right' as const,
      sortable: true,
    },
    { key: 'unit', label: '단위', width: '8%', align: 'center' as const },
    {
      key: 'status',
      label: '상태',
      width: '10%',
      render: (_: unknown, row: StockBalanceType) => {
        const item = getItemById(row.itemId);
        const status = item
          ? getStockStatus(row.quantity, item.minStock, item.maxStock)
          : 'normal';
        return <StatusBadge status={status} />;
      },
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="재고 현황"
        description="창고별 품목 재고를 확인합니다."
        actions={
          <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-accent">
            <Download className="w-4 h-4" />
            엑셀 다운로드
          </button>
        }
      />

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="품목코드, 품목명 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 창고 필터 */}
        <select
          value={selectedWarehouse}
          onChange={(e) => setSelectedWarehouse(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 창고</option>
          {warehouses.map((wh) => (
            <option key={wh.id} value={wh.id}>
              {wh.name}
            </option>
          ))}
        </select>

        {/* 상태 필터 */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value as StockStatus | 'all')}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          <option value="normal">정상</option>
          <option value="low">부족</option>
          <option value="out">품절</option>
          <option value="over">과잉</option>
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />
    </div>
  );
}

function StatusBadge({ status }: { status: StockStatus }) {
  const colors: Record<StockStatus, string> = {
    normal: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    low: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    out: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    over: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}>
      {STOCK_STATUS_LABELS[status]}
    </span>
  );
}
